import { useEffect, useState } from "react";

const STORAGE_KEY = "pfjudgepro-active-ballot";

export const createEmptyBallot = () => ({
  details: {
    resolution: "",
    tournament: "",
    roundNumber: "",
    proTeam: "Pro",
    conTeam: "Con",
  },
  notes: {},
  proScores: { clarity: 3, impact: 3, likelihood: 3, urgency: 3 },
  conScores: { clarity: 3, impact: 3, likelihood: 3, urgency: 3 },
  speakers: [
    { id: "pro-1", team: "pro", name: "Pro speaker 1", points: 27.5 },
    { id: "pro-2", team: "pro", name: "Pro speaker 2", points: 27.5 },
    { id: "con-1", team: "con", name: "Con speaker 1", points: 27.5 },
    { id: "con-2", team: "con", name: "Con speaker 2", points: 27.5 },
  ],
  bestSpeakerId: "auto",
  customCriteria: [],
});

const mergeBallot = (saved) => ({
  ...createEmptyBallot(),
  ...saved,
  details: { ...createEmptyBallot().details, ...saved.details },
  speakers: saved.speakers || createEmptyBallot().speakers,
});

export default function useBallotState(userId) {
  const [ballot, setBallot] = useState(createEmptyBallot);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (!userId) return undefined;
    const key = `${STORAGE_KEY}-${userId}`;
    const imported = JSON.parse(
      sessionStorage.getItem("pfjudgepro-open-round") || "null",
    );
    const saved = JSON.parse(sessionStorage.getItem(key) || "null");
    setBallot(
      imported
        ? mergeBallot(imported)
        : saved
          ? mergeBallot(saved)
          : createEmptyBallot(),
    );
    sessionStorage.removeItem("pfjudgepro-open-round");
    setIsHydrated(true);
    return undefined;
  }, [userId]);

  useEffect(() => {
    if (!userId || !isHydrated) return;
    sessionStorage.setItem(`${STORAGE_KEY}-${userId}`, JSON.stringify(ballot));
  }, [ballot, isHydrated, userId]);

  const updateDetails = (key, value) => {
    setBallot((current) => ({
      ...current,
      details: { ...current.details, [key]: value },
    }));
  };

  const updateNotes = (stage, side, value) => {
    setBallot((current) => ({
      ...current,
      notes: {
        ...current.notes,
        [stage]: { ...current.notes[stage], [side]: value },
      },
    }));
  };

  const updateScore = (side, key, value) => {
    const scoreKey = side === "pro" ? "proScores" : "conScores";
    setBallot((current) => ({
      ...current,
      [scoreKey]: { ...current[scoreKey], [key]: value },
    }));
  };

  const updateSpeaker = (speakerId, field, value) => {
    setBallot((current) => ({
      ...current,
      speakers: current.speakers.map((speaker) => {
        if (speaker.id !== speakerId) return speaker;
        const points = Math.max(25, Math.min(30, value || 25));
        return { ...speaker, [field]: field === "points" ? points : value };
      }),
    }));
  };

  const addCustomCriterion = () => {
    setBallot((current) => ({
      ...current,
      customCriteria: [
        ...current.customCriteria,
        {
          id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          name: "",
          pro_score: 3,
          con_score: 3,
        },
      ],
    }));
  };

  const updateCustomCriterion = (criterionId, field, value) => {
    setBallot((current) => ({
      ...current,
      customCriteria: current.customCriteria.map((criterion) =>
        criterion.id === criterionId
          ? { ...criterion, [field]: value }
          : criterion,
      ),
    }));
  };

  const removeCustomCriterion = (criterionId) => {
    setBallot((current) => ({
      ...current,
      customCriteria: current.customCriteria.filter(
        (criterion) => criterion.id !== criterionId,
      ),
    }));
  };

  const resetBallot = () => setBallot(createEmptyBallot());

  return {
    ballot,
    addCustomCriterion,
    isHydrated,
    removeCustomCriterion,
    resetBallot,
    updateDetails,
    updateCustomCriterion,
    updateNotes,
    updateScore,
    updateSpeaker,
    setBallot,
  };
}
